/**
 * Turning a computed model into the thing cm-api will store.
 *
 * A snapshot is the module's audit trail: what the model said, on a date, from
 * named inputs. Everything here is about making that record honest — which is
 * mostly about what NOT to send.
 */
import type { Portfolio } from '@andavo/staffing-engine';
import { ENGINE_VERSION } from '@andavo/staffing-engine';
import type { SnapshotPortfolio, StaffingReadSet } from '../api';
import type { StaffingModel } from './model';

/**
 * The engine's portfolios, reduced to account IDs.
 *
 * `Portfolio` carries full `Account` objects — name, spend, current owner.
 * Sending those would put a second copy of the customer book inside a JSON
 * column, which is precisely what `staffing_snapshot`'s schema comment says it
 * does not do: *"the book itself is NOT copied here — that would be a second,
 * unmanaged copy of the customer list with no retention story."* Sending them
 * anyway would make that comment false without changing a line of it.
 *
 * Ids are also all a replay needs. The spend that produced this assignment is
 * already pinned by the book fingerprint, so a replay recomputes from the live
 * book and compares WHO GOT WHAT — which is a comparison of ids.
 *
 * `pinned` stays separate from `assigned` because the engine distinguishes them:
 * one was distributed by the balancer and the other was a decision somebody
 * made, and a replay that merged them could not tell a balancer change from a
 * pin change.
 */
export function toSnapshotPortfolios(
  portfolios: readonly Portfolio[],
): SnapshotPortfolio[] {
  return portfolios.map((p) => ({
    csm: p.csm,
    tier: p.tier,
    assigned: p.assigned.map((a) => a.id),
    pinned: p.pinned.map((a) => a.id),
  }));
}

export interface SnapshotRequest {
  configVersion: number;
  portfolios: SnapshotPortfolio[];
  bookFingerprint: string;
  engineVersion: string;
  includeInactive: boolean;
  label: string | null;
}

/**
 * Why a model cannot be saved yet, or null when it can.
 *
 * Returned as a sentence rather than a boolean because every one of these is
 * something the reader has to act on, and "Save is disabled" is not actionable.
 */
export function snapshotBlocker(
  read: StaffingReadSet | null,
  model: StaffingModel | null,
): string | null {
  if (!read || !model) return 'The model has not finished loading.';
  if (!read.config.configured || read.config.config === null) {
    return 'There is no saved configuration to snapshot against.';
  }
  if (model.result.portfolios.length === 0) {
    return 'No portfolios were computed — a snapshot with no assignment is not a record of one.';
  }
  // The one that is a SERVER capability rather than a user mistake: cm-api
  // requires the digest and computes it itself, so a deployment that predates
  // that field cannot accept a snapshot from this page at all. Saying so beats
  // a 400 the reader cannot act on.
  if (!read.accounts.meta.bookFingerprint) {
    return 'This CRM API does not return a book fingerprint, so a snapshot could not be verified against the book it was computed from. Saving is unavailable until it is updated.';
  }
  return null;
}

/**
 * Build the POST body for a snapshot.
 *
 * `includeInactive` is passed in rather than read off `meta.activeOnly`: the
 * caller is the one that asked cm-api for a subset, and round-tripping the
 * answer through a negated field is how the two ends drift. cm-api requires
 * this for the same reason — the fingerprint digests a row SET, so which rows
 * is half of what it means.
 */
export function toSnapshotRequest(args: {
  read: StaffingReadSet;
  model: StaffingModel;
  includeInactive: boolean;
  label: string | null;
}): SnapshotRequest {
  const { read, model, includeInactive, label } = args;
  const version = read.config.config?.version;
  if (version === undefined) {
    throw new Error(
      'no configuration version to snapshot against — snapshotBlocker should have caught this',
    );
  }
  const fingerprint = read.accounts.meta.bookFingerprint;
  if (!fingerprint) {
    throw new Error(
      'no book fingerprint in the accounts response — snapshotBlocker should have caught this',
    );
  }

  const trimmed = label?.trim();
  return {
    configVersion: version,
    portfolios: toSnapshotPortfolios(model.result.portfolios),
    bookFingerprint: fingerprint,
    engineVersion: ENGINE_VERSION,
    includeInactive,
    // Empty becomes null, not "". A snapshot with a blank label should render as
    // unnamed, and an empty string renders as a name that is somehow invisible.
    label: trimmed ? trimmed : null,
  };
}
